import { ECoverageAreaType } from "enums/options/coverageArea";

export interface CoverageAreaDriverRowDto {
  driverId: string;
  priority?: number;
}

export interface CoverageAreaOrderDetailDto {
  id: string;
  name?: string;
  type: ECoverageAreaType;
  hasDriver?: boolean;
  enabled?: boolean;
  storeUri?: string;
  drivers?: CoverageAreaDriverRowDto[];
  dealer?: {
    id: string;
    enabled: boolean;
  };
  map?: {
    link?: string;
    geoJson?: object;
  } | null;
  startingPoint?: {
    longitude: number;
    latitude: number;
  } | null;
  whatsAppNumber?: string;
  timezone?: string;
  openingHours?: Array<{
    day: string;
    open: string | null;
    close: string | null;
    enabled: boolean;
  }>;
  holidayOpeningHours?: Array<{
    day: string;
    open: string | null;
    close: string | null;
    enabled: boolean;
  }>;
  holidays?: unknown[];
  isOpenToday?: boolean;
}

export type CoverageAreaOrderDetailResponse =
  | CoverageAreaOrderDetailDto
  | { data: CoverageAreaOrderDetailDto };

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function unwrapCoverageAreaPayload(
  raw: unknown,
): Record<string, unknown> | null {
  if (!isRecord(raw)) return null;
  if (isRecord(raw.data)) return raw.data;
  return raw;
}

function parseDriverRow(
  raw: unknown,
  fallbackPriority: number,
): CoverageAreaDriverRowDto | null {
  if (!isRecord(raw)) return null;

  const driver = isRecord(raw.driver) ? raw.driver : null;
  if (typeof driver?.id === "string" && driver.id) {
    return {
      driverId: driver.id,
      priority:
        typeof raw.priority === "number" ? raw.priority : fallbackPriority,
    };
  }

  if (typeof raw.driverId === "string" && raw.driverId) {
    return {
      driverId: raw.driverId,
      priority:
        typeof raw.priority === "number" ? raw.priority : fallbackPriority,
    };
  }

  const details = isRecord(raw.details) ? raw.details : null;
  if (typeof details?.id === "string" && details.id) {
    return {
      driverId: details.id,
      priority:
        typeof raw.priority === "number" ? raw.priority : fallbackPriority,
    };
  }

  if (typeof raw.id === "string" && raw.id) {
    return {
      driverId: raw.id,
      priority:
        typeof raw.priority === "number" ? raw.priority : fallbackPriority,
    };
  }

  return null;
}

export function normalizeCoverageAreaOrderDetail(
  raw: unknown,
): CoverageAreaOrderDetailDto | null {
  const payload = unwrapCoverageAreaPayload(raw);
  if (!payload || typeof payload.id !== "string") {
    return null;
  }

  const driversRaw = Array.isArray(payload.drivers) ? payload.drivers : [];
  const drivers = driversRaw
    .map((row, index) => parseDriverRow(row, index))
    .filter((row): row is CoverageAreaDriverRowDto => row !== null);

  return {
    ...(payload as unknown as CoverageAreaOrderDetailDto),
    id: payload.id,
    drivers,
    hasDriver:
      typeof payload.hasDriver === "boolean"
        ? payload.hasDriver
        : drivers.length > 0,
  };
}
